////////////////////////////////////////////////////////////////////////////////
// 결제창(iframe·팝업) → 부모 화면 결과 전달 브리지
//
// 결제창은 PG 도메인을 거쳐 우리 서버의 복귀 주소(RETURN_BASE)로 돌아온다. 그런데 이 사이트는
// www.daehanbooks.co.kr 와 daehanbooks.co.kr 두 호스트에서 똑같이 뜨고, 복귀 주소는 서버에
// www 없는 쪽으로 고정돼 있다. 사용자가 www 에서 결제하면 결제창은 다른 출처에 떨어지고,
// parent.checkpayComplete / opener.wpayComplete 직접 호출은 보안 오류로 막힌다 —
// 결제는 끝났는데 결제창만 빈 화면으로 남는 2026-09-09 운영 장애의 원인이다.
//
// 그래서 통로를 넷 연다. 어느 하나만 닿아도 된다. 받는 쪽은 처음 것만 쓴다.
//   1) 직접 호출        같은 출처일 때. 가장 빠르다.
//   2) postMessage("*") 출처가 달라도 닿는다. 받는 쪽이 event.origin 으로 "같은 사이트"인지 가린다.
//   3) BroadcastChannel 같은 출처의 다른 창. opener 가 끊긴 경우 대비.
//   4) localStorage     3) 미지원 브라우저 대비 (storage 이벤트).
////////////////////////////////////////////////////////////////////////////////

/**
 * 모바일 브라우저인가. 이 사이트의 결제 모듈들은 모바일에서 팝업을 쓰지 않는다(KCP·나이스·KSNET 관례).
 * iOS 사파리는 탭과 window.open 사이에 서버 호출이 끼면 팝업을 막고, 열려도 새 탭이라 돌아오는 연결이 끊긴다.
 */
export function isMobileBrowser() {
    return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");
}

/** 결제창이 팝업·iframe 이 아니라 같은 탭에서 열린 상태인가 (돌아갈 부모창이 없다). */
export function isStandalone() {
    try {
        return !window.opener && window.parent === window;
    } catch (error) {
        return true;
    }
}

/** www 유무만 다른 호스트를 같은 사이트로 본다. */
export function isSameSite(origin) {
    try {
        const strip = (host) => String(host || "").toLowerCase().replace(/^www\./, "");
        return strip(new URL(origin).hostname) === strip(location.hostname);
    } catch (error) {
        return false;
    }
}

/**
 * 결제창 안에서 부모에게 결과를 알린다.
 * @param {string} channel 통로 이름 (예: "checkpay-result")
 * @param {string} fnName 부모 window 에 걸린 직접 호출 함수명 (예: "checkpayComplete")
 * @param {object} payload 결과 (query 그대로)
 * @returns {boolean} 직접 호출이 닿았는지
 */
export function notifyParent(channel, fnName, payload) {
    let direct = false;
    const targets = [];
    try {
        if (window.parent && window.parent !== window) targets.push(window.parent);
    } catch (error) {
        // 무시
    }
    try {
        if (window.opener) targets.push(window.opener);
    } catch (error) {
        // 무시
    }

    for (const target of targets) {
        // 1) 직접 호출 — 다른 출처면 여기서 SecurityError 가 난다. 잡고 다음 통로로 간다.
        try {
            if (typeof target[fnName] === "function") {
                target[fnName](payload);
                direct = true;
            }
        } catch (error) {
            // 다른 출처
        }

        // 2) postMessage — 출처가 달라도 닿는다. 결과값은 민감정보가 아니다(resCode·주문 _id·메시지).
        try {
            target.postMessage({ type: channel, payload }, "*");
        } catch (error) {
            // 무시
        }
    }

    // 3) BroadcastChannel
    try {
        const bc = new BroadcastChannel(channel);
        bc.postMessage(payload);
        bc.close();
    } catch (error) {
        // 미지원
    }

    // 4) localStorage
    try {
        localStorage.setItem(channel, JSON.stringify({ ...payload, at: Date.now() }));
    } catch (error) {
        // 무시
    }

    return direct;
}

/**
 * 부모 화면에서 결과를 듣는다. 반환된 함수를 부르면 전부 해제된다.
 * @param {string} channel
 * @param {(payload: object) => void} handler
 * @returns {() => void} dispose
 */
export function listenParent(channel, handler) {
    const onMessage = (event) => {
        if (!isSameSite(event.origin)) return;
        if (event.data?.type !== channel) return;
        handler(event.data.payload || {});
    };
    window.addEventListener("message", onMessage);

    let bc = null;
    try {
        bc = new BroadcastChannel(channel);
        bc.onmessage = (event) => handler(event.data || {});
    } catch (error) {
        bc = null;
    }

    const onStorage = (event) => {
        if (event.key !== channel || !event.newValue) return;
        try {
            handler(JSON.parse(event.newValue));
        } catch (error) {
            // 무시
        }
    };
    window.addEventListener("storage", onStorage);

    return () => {
        window.removeEventListener("message", onMessage);
        window.removeEventListener("storage", onStorage);
        try {
            bc?.close();
        } catch (error) {
            // 무시
        }
    };
}

/** 이전 결제의 잔여값이 이번 결과로 오인되지 않게 지운다. */
export function clearChannel(channel) {
    try {
        localStorage.removeItem(channel);
    } catch (error) {
        // 무시
    }
}
